import { EditorState, TextSelection } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

import { STATIC_RICH_TEXT_ATTRIBUTE, StaticCopySource, drawFresh } from './staticRender';

/**
 * What a read-only editor does for the user that static rich text has to do itself. A static doc has no
 * editor behind it, so every behaviour below is ProseMirror's or Tiptap's, re-created on the static DOM and pinned
 * to the live editor by a test in staticRender.test.ts:
 *
 * - Triple-click selects the clicked block, or the clicked inline formula as one node (`selectOnTripleClick`).
 * - A selected formula gets the editor's selection ring (`markSelectedNodeViews`).
 * - Copy and cut write the editor's clipboard serialization (`copyStaticSelection`).
 * - The swap of a hovered doc to a live editor keeps WebKit's hover events intact (`holdRemovedPointerTarget`).
 *
 * Plain text without node views does not need these for long: it turns into a live editor on hover
 * (StaticRichText). Adding a behaviour here is a design decision, not a patch: each one is code that must follow
 * the editor across Tiptap and ProseMirror upgrades (see documentation/rich-text-rendering.md). Prefer keeping a
 * doc live when a node view needs editor behaviour that is not listed here.
 */

// A node view holding one of these takes its own clicks and keys.
const TAKES_INPUT = 'math-field, input, textarea, select, [contenteditable="true"]';

// The DOM of this schema's textblocks: paragraph, heading, code block.
const TEXT_BLOCK_SELECTOR = 'p, h1, h2, h3, h4, h5, h6, pre';

/**
 * Triple-click on static rich text selects what ProseMirror's triple-click selects in a live editor: the
 * clicked text block, or the clicked node view (an inline formula) as a whole. The browser's own paragraph
 * selection stops at the first MathML element, and every inline formula holds one (KaTeX's hidden
 * accessible copy), so a block with a formula would be cut short there, and a click on the formula would
 * select from it to past the end of the doc. A node view that takes input (a Cloze blank) keeps its clicks.
 * A native listener: node views are React portals, whose events never reach a React handler on the root.
 */
export function selectOnTripleClick(root: Element, event: MouseEvent) {
  if (event.detail !== 3 || event.button !== 0 || !(event.target instanceof Element)) return;
  const outer = event.target.closest('.react-renderer');
  const nodeView = outer && root.contains(outer) ? outer : null;
  if (event.target.closest(TAKES_INPUT) || nodeView?.querySelector(TAKES_INPUT)) return;
  const block = event.target.closest(TEXT_BLOCK_SELECTOR);
  if (!block || !root.contains(block)) return;
  event.preventDefault();
  const range = document.createRange();
  if (nodeView) range.selectNode(nodeView);
  else range.selectNodeContents(block);
  window.getSelection()?.removeAllRanges();
  window.getSelection()?.addRange(range);
}

function selectOnStaticTripleClick(event: MouseEvent) {
  if (event.detail !== 3 || !(event.target instanceof Element)) return;
  const content = event.target.closest(`[${STATIC_RICH_TEXT_ATTRIBUTE}]`);
  if (content) selectOnTripleClick(content, event);
}

const SELECTED_NODE_CLASS = 'ProseMirror-selectednode';
const HIDE_SELECTION_CLASS = 'ProseMirror-hideselection';

// Whether a DOM point is at the very start of `wrapper`: there ProseMirror maps a point inside a node view to
// the position before the node, anywhere else (bias 1) to the position after it.
function isAtStartOf(wrapper: Node, node: Node, offset: number) {
  if (offset !== 0) return false;
  for (let scan: Node | null = node; scan; scan = scan.parentNode) {
    if (scan === wrapper) return true;
    if (scan.previousSibling) return false;
  }
  return false;
}

// Whether a DOM point is at an edge of `wrapper`, as prosemirror-view's `isOnEdge` decides it (its end test
// holds only at the first level).
function isOnEdgeOf(wrapper: Node, node: Node, offset: number) {
  const size = (scan: Node) => (scan.nodeType === 3 ? (scan as Text).length : scan.childNodes.length);
  let atStart = offset === 0;
  let atEnd = offset === size(node);
  for (let scan: Node | null = node; scan && (atStart || atEnd); ) {
    if (scan === wrapper) return true;
    const parent: Node | null = scan.parentNode;
    if (!parent) return false;
    const index = Array.prototype.indexOf.call(parent.childNodes, scan);
    atStart = atStart && index === 0;
    atEnd = atEnd && index === size(parent);
    scan = parent;
  }
  return false;
}

/**
 * What a read-only editor would mark for the current DOM selection inside one static rich text, decided as
 * prosemirror-view and Tiptap decide it: the editor reads a selection only when both its ends are inside it;
 * a node view is selected (`ProseMirror-selectednode`, which draws e.g. the inline formula's ring) when the
 * selection covers it, with an end inside it mapped to the node's far side unless at its very start; a caret
 * inside an atom selects that node, and its editor then hides the native selection.
 */
function selectionMarks(selection: globalThis.Selection): Array<[Element, string]> {
  const anchorElement = selection.anchorNode?.nodeType === 1 ? selection.anchorNode : selection.anchorNode?.parentNode;
  const content = anchorElement instanceof Element ? anchorElement.closest(`[${STATIC_RICH_TEXT_ATTRIBUTE}]`) : null;
  const root = content?.querySelector('.ProseMirror');
  const { focusNode, focusOffset } = selection;
  if (!root || !focusNode || !root.contains(selection.anchorNode) || !root.contains(focusNode)) return [];

  if (selection.isCollapsed) {
    const focusElement = focusNode.nodeType === 1 ? (focusNode as Element) : focusNode.parentElement;
    const wrapper = focusElement?.closest('.react-renderer');
    if (!wrapper || !root.contains(wrapper) || wrapper.querySelector(TAKES_INPUT)) return [];
    if (isOnEdgeOf(wrapper, focusNode, focusOffset)) return [];
    return [
      [wrapper, SELECTED_NODE_CLASS],
      [root, HIDE_SELECTION_CLASS]
    ];
  }

  const range = selection.getRangeAt(0);
  const marks: Array<[Element, string]> = [];
  for (const wrapper of Array.from(root.querySelectorAll('.react-renderer'))) {
    if (!range.intersectsNode(wrapper)) continue;
    const startsBefore =
      !wrapper.contains(range.startContainer) || isAtStartOf(wrapper, range.startContainer, range.startOffset);
    const endsAfter =
      !wrapper.contains(range.endContainer) || !isAtStartOf(wrapper, range.endContainer, range.endOffset);
    if (startsBefore && endsAfter) marks.push([wrapper, SELECTED_NODE_CLASS]);
  }
  return marks;
}

let markedBySelection: Array<[Element, string]> = [];

function markSelectedNodeViews() {
  const selection = document.getSelection();
  const next = selection && selection.rangeCount > 0 ? selectionMarks(selection) : [];
  if (next.length === 0 && markedBySelection.length === 0) return;
  for (const [element, className] of markedBySelection) element.classList.remove(className);
  for (const [element, className] of next) element.classList.add(className);
  markedBySelection = next;
}

const childIndex = (node: Node) => Array.prototype.indexOf.call(node.parentNode?.childNodes ?? [], node);

/**
 * The editor position of a DOM point in a static doc. The static HTML is a copy of the renderer's DOM for the
 * same doc, so the point is found there by its child-index path and read with `posAtDOM`. A point inside a node
 * view, whose content only the static page renders, maps as ProseMirror maps it: to before the node at its very
 * start, after it anywhere else.
 */
function positionInRenderer(root: Element, view: EditorView, node: Node, offset: number): number | null {
  let container = node;
  let index = offset;
  let wrapper: Element | null | undefined = (node.nodeType === 1 ? (node as Element) : node.parentElement)?.closest(
    '.react-renderer'
  );
  while (wrapper?.parentElement?.closest('.react-renderer') && root.contains(wrapper.parentElement)) {
    wrapper = wrapper.parentElement.closest('.react-renderer');
  }
  if (wrapper && root.contains(wrapper) && wrapper.parentNode) {
    index = childIndex(wrapper) + (isAtStartOf(wrapper, node, offset) ? 0 : 1);
    container = wrapper.parentNode;
  }
  const path: number[] = [];
  for (let scan: Node = container; scan !== root; ) {
    const parent: Node | null = scan.parentNode;
    if (!parent) return null;
    path.unshift(childIndex(scan));
    scan = parent;
  }
  let target: Node | undefined = view.dom;
  for (const step of path) target = target?.childNodes[step];
  if (!target) return null;
  try {
    return view.posAtDOM(target, index, 1);
  } catch {
    return null;
  }
}

const copySources = new WeakMap<Element, StaticCopySource>();

function copyStaticSelection(event: ClipboardEvent) {
  const selection = document.getSelection();
  const { anchorNode, focusNode } = selection ?? {};
  if (!event.clipboardData || !selection || !anchorNode || !focusNode || selection.isCollapsed) return;
  let root = anchorNode.nodeType === 1 ? (anchorNode as Element) : anchorNode.parentElement;
  while (root && !copySources.has(root)) root = root.parentElement;
  const source = root && copySources.get(root);
  if (!root || !source || source.editor.isDestroyed || !root.contains(focusNode)) return;
  const { doc, editor } = source;
  // The shared editor has drawn other docs since; draw this one again so its DOM matches the static copy.
  drawFresh(editor, doc);
  const anchor = positionInRenderer(root, editor.view, anchorNode, selection.anchorOffset);
  const head = positionInRenderer(root, editor.view, focusNode, selection.focusOffset);
  if (anchor === null || head === null || anchor === head) return;
  // What a read-only editor's copy writes: the content of the selection it reads from the DOM (a text selection
  // between the two points, as for a pointer selection), serialized for the clipboard. The selection is set on
  // the editor too, because Tiptap's plain-text clipboard serializer reads the editor's selection.
  const textSelection = TextSelection.between(doc.resolve(anchor), doc.resolve(head), 1);
  editor.view.updateState(EditorState.create({ doc, plugins: editor.state.plugins, selection: textSelection }));
  const { dom, text } = editor.view.serializeForClipboard(textSelection.content());
  event.preventDefault();
  event.clipboardData.clearData();
  event.clipboardData.setData('text/html', dom.innerHTML);
  event.clipboardData.setData('text/plain', text);
}

/**
 * Copy from static rich text writes what a read-only editor's copy writes: ProseMirror's clipboard
 * serialization of the selected content, so a formula pastes as a formula (the math node's own markup, its LaTeX
 * as plain text) and text keeps the schema's markup only. The browser's copy would write KaTeX's rendering with
 * every element's computed style inlined, which an editor pastes as styled text runs. As in an editor, only a
 * selection that starts and ends inside one doc is handled. `root` is the doc's `.ProseMirror` element.
 */
export function registerStaticCopySource(root: Element, source: StaticCopySource) {
  copySources.set(root, source);
}

let isInstalled = false;

/**
 * Installs every emulated behaviour above, once per page. Each is one document listener rather than one per doc:
 * a mouse listener on each doc would make Chromium expose its wrapper to assistive technology, which then
 * announces every read-only text block as "clickable", and a selection or copy outside every static doc costs
 * one `closest()`.
 */
export function installStaticInteractions() {
  if (isInstalled) return;
  isInstalled = true;
  document.addEventListener('mousedown', selectOnStaticTripleClick);
  document.addEventListener('selectionchange', markSelectedNodeViews);
  document.addEventListener('copy', copyStaticSelection);
  document.addEventListener('cut', copyStaticSelection);
}

/**
 * The static-to-live swap removes the node under the pointer. WebKit keeps that node as the origin of its next
 * boundary events even once it is detached, and from a detached node it fires no mouseout or mouseleave at
 * all: an ancestor with a native hover listener (Mantine's useHover, as on question answers) would stay
 * hovered after the pointer leaves it. The removed node is put back inside the root, emptied and hidden, until
 * the browser reports the pointer over another node. Returns the release, or null when nothing was removed.
 */
export function holdRemovedPointerTarget(root: HTMLElement, pointerTarget: EventTarget | null): (() => void) | null {
  if (!(pointerTarget instanceof Element) || pointerTarget.isConnected) return null;
  const node: Element = pointerTarget;
  for (const { name } of Array.from(node.attributes)) node.removeAttribute(name);
  node.replaceChildren();
  node.setAttribute('hidden', '');
  node.setAttribute('style', 'display: none');
  root.append(node);
  // The mouseover of the event that went live may still target the held node.
  function onMouseOver(event: MouseEvent) {
    if (event.target !== node) release();
  }
  function release() {
    document.removeEventListener('mouseover', onMouseOver, true);
    node.remove();
  }
  document.addEventListener('mouseover', onMouseOver, true);
  return release;
}
